/* Accepted PDP gallery state. Native Dawn keeps media selection, modal and commerce ownership. */
(() => {
  const policy = Object.freeze({
    canPreview: ({ compatible, primaryActive, hasWrist, hoverCapable, keyboardFocus }) =>
      Boolean(compatible && primaryActive && hasWrist && (hoverCapable || keyboardFocus)),
    variant: (variants, id) => variants.find((item) => String(item.id) === String(id)) || null,
  });
  window.ModTimeCatalogPolicy = policy;
  if (customElements.get('mt-cat-controller')) return;
  customElements.define('mt-cat-controller', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      // Use Shopify's normal form POST so a third-party fetch hook cannot
      // swallow a successful add and leave the customer without confirmation.
      this.addEventListener('submit', (event) => {
        if (event.target.matches('form[data-type="add-to-cart-form"]')) event.stopPropagation();
      }, { capture: true, signal: this.abort.signal });
      try { this.variants = JSON.parse(this.querySelector('[data-mt-cat-variants]')?.textContent || '[]'); }
      catch { this.variants = []; }
      this.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')) {
          this.variantPending = true;
          this.resetPreview();
          this.setWristVisibility(false);
          const gallery = this.querySelector('[data-mt-cat-gallery]');
          const primary = gallery?.querySelector('[data-mt-cat-primary]');
          if (primary && gallery.setActiveMedia) gallery.setActiveMedia(primary.dataset.mediaId, false);
        }
        if (event.target.matches('input[name="id"]')) { this.variantPending = false; this.refresh(); }
      }, { capture: true, signal: this.abort.signal });
      this.addEventListener('pointerover', (event) => {
        const primary = event.target.closest('[data-mt-cat-primary]');
        if (primary && !primary.contains(event.relatedTarget)) this.showPreview(false);
      }, { signal: this.abort.signal });
      this.addEventListener('pointerout', (event) => {
        const primary = event.target.closest('[data-mt-cat-primary]');
        if (primary && !primary.contains(event.relatedTarget)) this.resetPreview();
      }, { signal: this.abort.signal });
      this.addEventListener('focusin', (event) => {
        if (event.target.closest('[data-mt-cat-primary]')) this.showPreview(true);
      }, { signal: this.abort.signal });
      this.addEventListener('focusout', () => this.resetPreview(), { signal: this.abort.signal });
      this.addEventListener('scroll', () => this.resetPreview(), { capture: true, passive: true, signal: this.abort.signal });
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, { childList: true, subtree: true });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) === String(this.querySelector('product-info')?.dataset.section)) { this.variantPending = false; this.refresh(); }
        });
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.resetPreview();
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.observer = null;
      this.unsubscribe?.(); this.unsubscribe = null;
    }
    currentVariant() {
      return policy.variant(this.variants, this.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value);
    }
    setWristVisibility(visible) {
      const gallery = this.querySelector('[data-mt-cat-gallery]');
      if (!gallery) return;
      if (gallery.dataset.wristReference) visible = true;
      const key = gallery.dataset.wristKey;
      let changed = false;
      gallery.querySelectorAll('[data-media-id], [data-target]').forEach((node) => {
        if ((node.dataset.mediaId || node.dataset.target || '').endsWith('-' + key)) {
          if (node.hidden === visible) { node.hidden = !visible; changed = true; }
        }
      });
      document.getElementById('ProductModal-' + this.querySelector('product-info')?.dataset.section)?.querySelectorAll('[data-media-id]').forEach((node) => {
        if (node.dataset.mediaId === key) {
          node.setAttribute('data-mt-cat-wrist', '');
          if (node.hidden === visible) node.hidden = !visible;
        }
      });
      if (changed) {
        gallery.querySelector('[id^="GalleryViewer-"]')?.resetPages?.();
        gallery.querySelector('[id^="GalleryThumbnails-"]')?.resetPages?.();
      }
    }
    refresh() {
      if (this.variantPending) { this.compatible = false; this.setWristVisibility(false); return; }
      const variant = this.currentVariant();
      const gallery = this.querySelector('[data-mt-cat-gallery]');
      const styleIds = (gallery?.dataset.wristStyleIds || '').split(',');
      this.compatible = Boolean(variant?.wrist_compatible || gallery?.dataset.wristReference || (variant && styleIds.includes(String(variant.id))));
      if (!this.compatible) this.resetPreview();
      this.setWristVisibility(this.compatible);
      if (!variant) return;
      const values = {
        case_size: variant.case_size_mm == null ? '' : variant.case_size_mm + ' mm',
        water: variant.water_atm == null ? '' : variant.water_atm + ' ATM',
        movement: variant.movement || '',
      };
      document.querySelectorAll('[data-mt-cat-fact]').forEach((node) => {
        const value = values[node.dataset.mtCatFact];
        if (value && node.textContent !== value) node.textContent = value;
      });
    }
    showPreview(keyboardFocus) {
      const gallery = this.querySelector('[data-mt-cat-gallery]');
      const primary = gallery?.querySelector('[data-mt-cat-primary]');
      const wrist = [...(gallery?.querySelectorAll('[data-media-id]') || [])].find((node) =>
        node.dataset.mediaId.endsWith('-' + gallery.dataset.wristKey) && !node.hidden);
      if (!policy.canPreview({ compatible: this.compatible, primaryActive: primary?.classList.contains('is-active'), hasWrist: Boolean(wrist?.querySelector('img')), hoverCapable: matchMedia('(hover:hover) and (pointer:fine)').matches, keyboardFocus })) return;
      this.resetPreview();
      const surface = primary.querySelector('.product__media');
      const button = primary.querySelector('button[data-media-id]');
      if (!surface || !button) return;
      this.overlay = wrist.querySelector('img').cloneNode(true);
      this.overlay.removeAttribute('id');
      this.overlay.className = 'mt-cat-hover-image';
      this.overlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;background:#fff;pointer-events:none;z-index:1';
      surface.append(this.overlay);
      if ((gallery.dataset.wristReference || gallery.dataset.wristStyleIds) && wrist.querySelector('.mt-gmt-media-caption')) {
        this.previewCaption = wrist.querySelector('.mt-gmt-media-caption').cloneNode(true); this.previewCaption.classList.add('mt-cat-hover-caption'); surface.append(this.previewCaption);
      }
      this.zoomButton = button;
      this.originalMediaId = button.dataset.mediaId;
      button.dataset.mediaId = gallery.dataset.wristKey;
      primary.dataset.mtCatVisibleMedia = gallery.dataset.wristKey;
    }
    resetPreview() {
      this.overlay?.remove(); this.overlay = null;
      this.previewCaption?.remove(); this.previewCaption = null;
      if (this.zoomButton && this.originalMediaId) {
        const button = this.zoomButton;
        const original = this.originalMediaId;
        const visible = button.dataset.mediaId;
        const modal = document.getElementById('ProductModal-' + this.querySelector('product-info')?.dataset.section);
        const restore = () => { if (button.dataset.mediaId === visible) button.dataset.mediaId = original; };
        // Dawn moves focus before ProductModal reads openedBy's media id.
        // Let that synchronous read finish before restoring the hover opener.
        if (modal?.hasAttribute('open')) queueMicrotask(restore);
        else restore();
      }
      this.zoomButton?.closest('[data-mt-cat-primary]')?.removeAttribute('data-mt-cat-visible-media');
      this.zoomButton = null; this.originalMediaId = null;
    }
  });
})();
